import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AuthService,
    Block,
    BlockService,
    PageService,
    PageType,
    scrollToTop,
    SettingsService,
    SpinnerService,
} from '@gallaghercp/core-frontend';
import { Observable, Subscription, tap, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { APP_CONFIG } from '../../app.config';
import { ClientService } from '../../services/client.service';

@Component({
    selector: 'core-login',
    templateUrl: './login.component.html',
    standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
    protected auth = inject(AuthService);
    protected pages = inject(PageService);
    protected api = inject(HttpClient);
    protected spinner = inject(SpinnerService);
    client = inject(ClientService);
    protected settings = inject(SettingsService);
    protected router = inject(Router);
    protected route = inject(ActivatedRoute);
    protected titleService = inject(Title);
    protected breakpoint = inject(BreakpointObserver);

    copyright: string = APP_CONFIG?.copyright || '';
    appName: string = APP_CONFIG?.appName || '';
    loginBulletin?: { text?: string; linkHref?: string; linkText?: string } = (APP_CONFIG as any)['loginBulletin'];

    loading: boolean = false;
    errorMessage?: string | boolean;
    successMessage?: string | boolean;
    loginType?: string;
    passwordType = 'password';
    subscriptions: Subscription[] = [];
    darkMode?: boolean;
    managedPage$?: Observable<PageType>;
    loginForm = new UntypedFormGroup({
        username: new UntypedFormControl('', [Validators.required, Validators.email]),
        password: new UntypedFormControl('', [Validators.required, Validators.minLength(5)]),
    });
    protected readonly APP_CONFIG = APP_CONFIG;

    ngOnInit() {
        this.client.hideSessionModals();
        this.subscriptions.push(
            this.settings.isDarkMode().subscribe((state: any) => {
                this.darkMode = state;
            }),
        );
        this.subscriptions.push(
            this.settings.isDarkModeEnabled().subscribe((state: any) => {
                if (state === false && this.darkMode) {
                    this.settings.toggle('isDarkMode');
                }
            }),
        );

        this.titleService.setTitle('Login - ' + APP_CONFIG.appName);

        this.subscriptions.push(
            this.auth
                .validateAuthentication()
                .pipe(
                    switchMap(() => {
                        if (this.auth.isAuthenticated()) {
                            this.router.navigate(['/']);
                        }
                        return this.client.getAccessType();
                    }),
                    tap((type: any) => {
                        this.loginType = type;
                        if (this.loginType === 'public' && this.router.url === '/login') {
                            this.router.navigate(['/']);
                        }
                        if (this.loginType === 'generic' || this.loginType === 'generic-with-username') {
                            this.loginForm.get('username')!.setValidators([]);
                        }
                        if (this.loginType === 'individual-managed') {
                            this.managedPage$ = this.pages.getPage('login');
                        }
                    }),
                    switchMap(() => this.auth.initialise()),
                )
                .subscribe(),
        );
    }

    togglePasswordType() {
        this.passwordType = this.passwordType === 'text' ? 'password' : 'text';
    }

    login() {
        this.loading = true;
        this.auth.clearAuthMessaging();
        this.errorMessage = false;
        this.successMessage = false;
        this.spinner.show('login');
        return this.auth
            .login(this.loginForm.get('username')!.value, this.loginForm.get('password')!.value)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    scrollToTop(500);
                    this.spinner.hide('login');
                    this.loading = false;
                    if (
                        error.status > 0 &&
                        error.status !== 418 &&
                        error.status !== 419 &&
                        error.status !== 420 &&
                        error.status !== 421
                    ) {
                        this.errorMessage =
                            this.loginType === 'generic' || this.loginType === 'generic-with-username'
                                ? `<p>Please check the password is correct, and try again.</p>
                                    <p>Error reference: ${error.headers.get('x-service-request-id')}</small></p>`
                                : `<p>Please check your email address and password are correct, and try again.</p>
                                    <p>Error reference: ${error.headers.get('x-service-request-id')}</small></p>`;
                        this.auth.setAuthMessaging('error', this.errorMessage);
                    }
                    return throwError(() => {
                        return `Error Code: ${error.status}\nMessage: ${error.message}`;
                    });
                }),
            )
            .subscribe(() => {
                this.spinner.show('login');
                this.loading = true;
            });
    }

    getSiteName() {
        return this.client.getSiteName();
    }

    getManagedPage$(): Observable<PageType> | undefined {
        return this.pages.getPage('login');
    }

    trackByFn(idx: number, item: any) {
        return item.uid + '_' + idx;
    }

    getBlockClassPosition(block: Block, previousBlock?: Block) {
        return BlockService.getBlockClassPosition(block, previousBlock);
    }

    getBlockClasses(block: Block) {
        return BlockService.getBlockClasses(block);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }
}
